import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ClassicPreviewProps {
    // Ordered slide preview URLs (one per selected image, tour order).
    photoUrls: string[];
    // The selected address-overlay asset URL (full-frame PNG), or null for none.
    overlayUrl: string | null;
}

// Live preview for the Classic (Video 1) slideshow: the selected photos shown
// one at a time, with the optional address overlay composited full-frame on top
// — mirroring the legacy image-to-video flow (overlay scaled to 1280x720,
// overlay=0:0). No V2 text/colour/font templates apply here.
const ClassicPreview: React.FC<ClassicPreviewProps> = ({ photoUrls, overlayUrl }) => {
    const [current, setCurrent] = useState(0);
    const count = photoUrls.length;
    const index = count ? ((current % count) + count) % count : 0;
    const photo = photoUrls[index];

    const go = (delta: number): void => {
        if (count) setCurrent((c) => (c + delta + count) % count);
    };

    return (
        <div className="space-y-3">
            <div>
                <h4 className="text-base font-semibold text-primary">Live Preview</h4>
                <p className="text-sm text-neutral-500">
                    Step through your photos with the optional address overlay.
                </p>
            </div>

            <div className="mx-auto max-w-2xl">
                <div className="relative aspect-video overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100">
                    {photo ? (
                        <img
                            src={photo}
                            alt={`Slide ${index + 1}`}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm text-neutral-500">
                            No photos selected
                        </div>
                    )}
                    {overlayUrl && (
                        <img
                            src={overlayUrl}
                            alt="Address overlay"
                            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                        />
                    )}
                </div>

                <div className="mt-3 grid grid-cols-3 items-center gap-3">
                    <button
                        type="button"
                        onClick={() => go(-1)}
                        disabled={count < 2}
                        className="flex items-center gap-1 justify-self-start rounded-md border border-neutral-200 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:border-secondary disabled:opacity-50"
                        aria-label="Previous photo"
                    >
                        <ChevronLeft className="h-4 w-4 text-secondary" />
                    </button>

                    <span className="text-center text-xs font-semibold text-primary">
                        Photo {count ? index + 1 : 0} / {count}
                    </span>

                    <button
                        type="button"
                        onClick={() => go(1)}
                        disabled={count < 2}
                        className="flex items-center gap-1 justify-self-end rounded-md border border-neutral-200 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:border-secondary disabled:opacity-50"
                        aria-label="Next photo"
                    >
                        <ChevronRight className="h-4 w-4 text-secondary" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClassicPreview;
